import type { Task } from "~skillBuilder/utils/types";

import { title } from "../../contentBlocks";

export const AzMrtReporting = {
  name: "Reporting Procedures",
  uniqueName: "az-mrt-reporting",
  icon: "Lesson",
  durationTimeText: "5 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start Lesson",
      questions: [
        // Question 1.1
        {
          attribute: "az-mrt.reporting/overview",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Understanding Your Reporting Obligation", durationTimeText: "5 min" })}

You play a vital role in keeping students safe. As a substitute teacher, it's essential to understand your legal obligations as a mandated reporter. This lesson will help you know when, how, and what to report if you suspect abuse or neglect, explain the protections in place for reporters, and outline what happens after a report is made.
`,
          },
        },
      ],
    },
    // Step 2
    {
      questions: [
        // Question 2.1
        {
          attribute: "az-mrt.reporting/legal",
          label: "Your Legal Duty to Report",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `

As a substitute teacher in Arizona, you have a legal obligation to report suspected child abuse or neglect. The standard is "reasonable belief" - you don't need proof or certainty. Your role is not to investigate or confirm abuse, but to report reasonable suspicions.

Importantly, you cannot delegate this responsibility to someone else. Even if you tell a principal or another teacher, you still have a personal legal obligation to ensure a report is made. Failure to report is a criminal offense in Arizona.

<ul style="margin-left: 16px;">
<li>🧠 <strong>Reasonable Belief:</strong> You do not need proof or confirmation—just a reasonable suspicion based on what you see or hear.</li>
<li>❌ <strong>You Cannot Delegate This Responsibility:</strong> Telling a colleague or principal does not fulfill your legal obligation. You must personally ensure the report is made.</li>
<li>☎️ <strong>You MUST Report Any Suspected Abuse or Neglect:</strong> No matter how uncertain you feel, reporting is always the right step.</li>
<li>⚖️ <strong>Failure to Report = Criminal Offense</strong>
  <ul style="margin-left: 16px;">
  <li>Class 1 misdemeanor for failure to report</li>
  <li>Class 6 felony if failure to report involves certain "reportable offenses" (e.g., sexual offenses against minors)</li>
  </ul>
</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 3
    {
      questions: [
        // Question 3.1
        {
          attribute: "az-mrt.reporting/when",
          label: "When to Report",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `

When it comes to reporting suspected abuse or neglect, time is of the essence. You must report immediately whenever you notice any warning signs or hear disclosures. Even if you aren't 100% sure, your reasonable cause to believe is enough to trigger a report.

For example, if you see unexplained bruises on a student, hear a child mention something concerning, or witness interactions that raise red flags, that's your cue to act right away.

Remember, you are not an investigator — your role is to report your concerns so the proper authorities can look into it. Delaying or trying to gather proof yourself can put a child at further risk, and legally, failure to report is a serious offense.

If in doubt, report! It's always better to be safe and protect the child's well-being.

**Report immediately when:**
<ul style="margin-left: 16px;">
<li>⭐ You observe signs of abuse or neglect</li>
<li>⭐ A child discloses abuse or neglect to you</li>
<li>⭐ You have reasonable belief abuse or neglect occurred</li>
</ul>

⚠️ **Remember:**
<ul style="margin-left: 16px;">
<li>Do NOT wait for proof or certainty</li>
<li>Do NOT try to investigate yourself</li>
<li>Do NOT delay making your report</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 4
    {
      questions: [
        // Question 4.1
        {
          attribute: "az-mrt.reporting/how",
          label: "How to Report",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `

To report suspected child abuse or neglect in Arizona, call the Arizona DCS Hotline: 1-888-SOS-CHILD (1-888-767-2445). This hotline is available 24 hours a day, 7 days a week, 365 days a year. Alternatively, you can report to local law enforcement.

After calling the hotline (or before, if it won't delay reporting), follow your school district's protocol. This typically involves notifying a designated school official, such as the principal or school counselor. However, remember that notifying school officials does not fulfill your legal obligation to report to DCS. Always document your report, including the date, time, and the person you spoke with.

📞 **Call Arizona DCS Hotline:** 1-888-SOS-CHILD (1-888-767-2445) 24 hrs/day

📝 **Document Your Report** - Record the date, time, who you spoke with, and the validation number (if provided)
`,
          },
        },
      ],
    },
    // Step 5
    {
      questions: [
        // Question 5.1
        {
          attribute: "az-mrt.reporting/info",
          label: "What Information to Provide",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `

When making a report, be prepared to provide as much information as possible. They may ask you questions that you don't know the answers to. It's okay if you don't have all the answers—just share what you know. Include the child's name and age, the nature and extent of any injuries or concerning conditions, details about this or any previous incidents, and any other information that might assist investigators.

While you can report anonymously, providing your name and contact information can be helpful if investigators need additional information. Remember, your identity as a reporter is confidential and cannot be released without a court order. The more specific information you can provide, the better equipped DCS will be to assess the situation and protect the child.

**Be ready to share these important details when reporting:**

<ul style="margin-left: 16px;">
<li>👧 <strong>Child's name and details</strong> such as age, and address (if available) - Help them identify exactly who needs protection.</li>
<li>🩹 <strong>Nature and extent of injuries or condition</strong> - Describe what you see or hear clearly.</li>
<li>📋 <strong>Any previous incidents</strong> - Share any past concerns you might know about.</li>
<li>📝 <strong>Your name and contact info</strong> (optional but helpful) - It helps follow up, though you can stay anonymous.</li>
<li>📌 <strong>Any other details that seem relevant</strong> - Trust your instincts—no detail is too small to mention!</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 6
    {
      questions: [
        // Question 6.1
        {
          attribute: "az-mrt.reporting/protections",
          label: "Legal Protections for Reporters",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `

Arizona law provides important protections for mandated reporters.

<ul style="margin-left: 16px;">
<li>🛡️ <strong>Immunity from Liability:</strong> Good-faith reporters cannot be sued or prosecuted</li>
<li>⚖️ <strong>Employment Protection:</strong> You cannot be fired or disciplined for reporting in good faith</li>
<li>🤫 <strong>Confidentiality Guaranteed:</strong> Reporter identity is protected by law, except by court order</li>
<li>🕵️ <strong>Anonymous Reporting:</strong> Allowed, though giving contact info helps investigators</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 7
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 7.1
        {
          attribute: "az-mrt.reporting/after",
          label: "What Happens After You Report",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `

After you make a report, DCS takes over the process.

<ul style="margin-left: 16px;">
<li>📋 <strong>Screening & Classification</strong> - DCS reviews your report to decide if it meets the criteria for investigation.</li>
<li>🗣️ <strong>Investigation Process</strong> - Investigator may interview the child, family members, school staff, and others. They may also gather evidence and consult with professionals.</li>
<li>⏰ <strong>Investigation Timeline</strong> - DCS must enter a finding within 45 days of the report</li>
</ul>

**Possible DCS Findings:**

✅ **Substantiated (Proposed Substantiated)**
<ul style="margin-left: 16px;">
<li>Evidence supports abuse/neglect under a "preponderance of evidence" standard</li>
<li>The alleged perpetrator is notified and has the right to appeal before the finding is entered in the DCS Central Registry</li>
</ul>

⚠️ **Unsubstantiated**
<ul style="margin-left: 16px;">
<li>Evidence does not support the allegation</li>
<li>Case closed, but services may still be offered</li>
</ul>

❌ **Unable to Locate**
<ul style="margin-left: 16px;">
<li>DCS could not locate the child despite reasonable efforts, making investigation incomplete</li>
<li>This finding is not appealable</li>
</ul>

💡 **Remember:** Your responsibility ends once you make the report—DCS handles the investigation and determination.
`,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
